import { EmbeddableMetricDataQualityWarning } from '@tableau/data-catalog-client-components';
import { ButtonType, ButtonWidget, CompactInlineAlertWidget, InlineAlertType, InlineAlertWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { LocationField } from '~src/app/spaces/Tooltips/Elements/LocationField';

import { DetailsTooltipStyles } from '../../app/spaces/CardView/DetailsTooltipStyles';
import { MarkupField } from '../../app/spaces/Components/MarkupField';
import { Name } from '../../app/spaces/Tooltips/Elements/Name';
import { RelatedEntity } from '../../app/spaces/Tooltips/Elements/RelatedEntity';
import { Tags } from '../../app/spaces/Tooltips/Elements/Tags';
import { Timestamp } from '../../app/spaces/Tooltips/Elements/Timestamp';
import { Tooltip } from '../../app/spaces/Tooltips/Elements/Tooltip';
import { User } from '../../app/spaces/Tooltips/Elements/User';
import { ContentTooltipProps, getPersonalSpaceLink,getProjectLink, TooltipDescriptionTestId } from '../../app/spaces/Tooltips/TooltipHelper';
import { MapDispatchToProps, MapStateToProps } from '../../app/utils/ConnectUtils';
import { CatalogApiRequests } from '../../catalog/CatalogApiRequests';
import { catalogApolloClient } from '../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { selectCatalogingOnSiteEnabled } from '../../catalog/selectors/CatalogSelectors';
import { Messages } from '../../codegen/Localize';
import { DeprecatedInterpolatedString } from '../../common/react/components/InterpolatedString';
import { HelpService } from '../../commonInfra/services/HelpService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { L10n } from '../../ts/L10n/L10n';
import { IUserInfo } from '../../ts/models/IUserInfo';
import { DataCatalogUIActionsService } from '../../ts/services/DataCatalogUIActionsService';
import { resumeMetricFromExplorer } from '../actions/UpdateMetricsActions';
import { IMetricInfo } from '../models/IMetricInfo';
import * as MetricsSelectors from '../selectors';

interface MetricTooltipStateProps {
  userCanResumeSuspendedMetric: boolean;
  catalogingOnSiteEnabled: boolean;
}

interface MetricTooltipDispatchProps {
  resumeMetricFromExplorer: (metricId: string) => void;
}

export type MetricTooltipProps = MetricTooltipStateProps & MetricTooltipDispatchProps & ContentTooltipProps;

export function MetricTooltip(props: MetricTooltipProps): JSX.Element {
  const { urls } = props;
  const content = props.content as IMetricInfo;

  const owner: IUserInfo = content.owner;

  // workaround for the incompleteness of the original metric content type.
  const description = content.description;
  const tags = content.tags;

  /* Just commenting this out in case we wanted to quickly add connectedView's back in.
  const connectedView = content.connectedView;

  let connectedViewLink: EntityLinkProps;
  if (connectedView) {
    const viewName = connectedView.isViewModified ? connectedView.name + '*' : connectedView.name;
    connectedViewLink = {
      href: urls.viewUrl(connectedView.path),
      iconImagePath: null,
      name: viewName
    };
  } else {
    connectedViewLink = {
      name: Messages.unavailable(),
      textStyleOverride: GreyedOutTextStyle,
      href: '',
      iconImagePath: null
    };
  }
  */

  const projectLink = getProjectLink(content.project, urls);
  const personalSpaceLink = getPersonalSpaceLink(urls);
  const locationType = content.location?.type;
  const ownerId = content.owner?.id;

  return (
    <Tooltip>
      <Name name={content.name} />
      {content.suspended && <MetricSuspendedWarning {...props} />}
      {renderDataQualityWarnings(props)}
      <MarkupField markup={description}
                   testId={TooltipDescriptionTestId}
                   extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]} />
      <Tags tags={tags} />
      {/*<RelatedEntity label={Messages.metricConnectedView()} link={connectedViewLink}/>*/}
      {!FeatureFlags.personalSpaceEnabled && !props.inProjectSpace && <RelatedEntity label={Messages.project()} link={projectLink} /> }
      {FeatureFlags.personalSpaceEnabled && <LocationField inProjectSpace={props.inProjectSpace} inPersonalSpace={props.inPersonalSpace}
        locationType={locationType} projectLink={projectLink} personalSpaceLink={personalSpaceLink} contentOwnerId={ownerId}/>}
      <User user={owner} label={Messages.owner()} urls={urls} />
      <CompactInlineAlertWidget
        type={InlineAlertType.Info}
        testId='metric-authentication-info-alert'
        style={{marginBottom: '18px'}}
        children={
          <>{Messages.metricsAuthenticationInfo()}
            {' '}
            <TextLinkWidget href={HelpService.getServerHelpUrlByCategory('metrics-data')} testId="link">{Messages.metricsAuthenticationInfoLinkText()}</TextLinkWidget>
          </>
        }
        />

      {/* "Refreshed": Last time the metric data store gets updated
          "Modified": last time the metric itself was modified */}
      {props.lastUpdatedTimestamp && <Timestamp label={Messages.metricLastUpdated()} value={props.lastUpdatedTimestamp} />}
    </Tooltip>
  );
}

function renderDataQualityWarnings(props: MetricTooltipProps): JSX.Element {
  const content = props.content as IMetricInfo;
  if (FeatureFlags.metricsDataQualityWarningsEnabled && props.catalogingOnSiteEnabled) {
    return (
      <div style={{marginBottom: '18px'}}>
        <EmbeddableMetricDataQualityWarning
          apiRequests={CatalogApiRequests}
          uiActions={DataCatalogUIActionsService.instance()}
          siteUrls={props.urls} featureFlags={catalogFeatureFlagsAdapter()}
          client={catalogApolloClient} metricLuid={content.luid} side={'left'}
        />
      </div>
    );
  }
}

function MetricSuspendedWarning(props: MetricTooltipProps): JSX.Element {
  const inlineAlertProps = {
    testId: 'metrics-suspended',
    type: InlineAlertType.Warning,
    style: { marginBottom: '18px' }
  };

  if (props.userCanResumeSuspendedMetric) {
    const resumeButton = (
      <ButtonWidget
        buttonType={ButtonType.Outline}
        testId="metrics-resume"
        onClick={() => props.resumeMetricFromExplorer(props.content.id)}
      >
        {Messages.metricResumeButtonLabel()}
      </ButtonWidget>
    );

    return (
      <InlineAlertWidget {...inlineAlertProps} button={resumeButton}>
        {renderSuspendedWarningWithResumeContent(props)}
      </InlineAlertWidget>
    );

  } else {
    return (
      <InlineAlertWidget {...inlineAlertProps}>
        {Messages.metricSuspendedNoResumeTooltip()}
      </InlineAlertWidget>
    );
  }
}

function renderSuspendedWarningWithResumeContent(props: MetricTooltipProps): JSX.Element {
  const learnMoreLink = (
    <TextLinkWidget
      href={HelpService.getServerHelpUrlByCategory('metrics-suspend')}
      target="_blank"
      testId="metrics-resume-help"
    >
      {Messages.learnMore()}
    </TextLinkWidget>
  );

  return (
    <DeprecatedInterpolatedString
      baseStringKey="metricSuspendedWithResumeTooltip"
      translate={() => Messages.metricSuspendedWithResumeTooltip({LEARN_MORE_LINK: '{LEARN_MORE_LINK}'})}
      replacements={{
        'SUSPEND_DATE': () => <span>{L10n.localizedDate(props.lastUpdatedTimestamp)}</span>,
        'LEARN_MORE_LINK': () => learnMoreLink,
      }}
    />
  );
}

const mapStateToProps: MapStateToProps<MetricTooltipStateProps, ContentTooltipProps> = (state, ownProps) => ({
  userCanResumeSuspendedMetric: MetricsSelectors.userCanResumeSuspendedMetric(state, ownProps.content),
  catalogingOnSiteEnabled: selectCatalogingOnSiteEnabled(state)
});

const mapDispatchToProps: MapDispatchToProps<MetricTooltipDispatchProps> = (dispatch) => bindActionCreators({
  resumeMetricFromExplorer
}, dispatch);

export const ConnectedMetricTooltip = connect(mapStateToProps, mapDispatchToProps)(MetricTooltip);
